import { Component, Input } from "@angular/core";
import { CommonModule } from "@angular/common";
import { SkeletonModule } from "primeng/skeleton";
import { TableModule } from "primeng/table";

export interface TableSkeletonColumn {
  width: string;
  height?: string;
  shape?: "rectangle" | "circle";
  align?: "left" | "center" | "right";
}

/**
 * Standalone table skeleton component with its own p-table.
 * Use this when you need a complete skeleton table outside of an existing p-table.
 * For use inside p-table loadingbody templates, use TableSkeletonRowsComponent instead.
 */
@Component({
  selector: "app-table-skeleton",
  standalone: true,
  imports: [CommonModule, SkeletonModule, TableModule],
  template: `
    <p-table
      [value]="rowsArray"
      [scrollable]="true"
      scrollDirection="both"
      size="small"
      styleClass="p-datatable-skeleton"
    >
      <ng-template pTemplate="header">
        <tr>
          @for (col of columns; track $index) {
            <th
              [style.width]="col.width"
              [class.text-center]="col.align === 'center'"
              [class.text-right]="col.align === 'right'"
            >
              <p-skeleton
                [width]="getHeaderWidth(col)"
                height="1rem"
                [shape]="col.shape === 'circle' ? 'circle' : 'rectangle'"
              />
            </th>
          }
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-row>
        <tr>
          @for (col of columns; track $index) {
            <td
              [class.text-center]="col.align === 'center'"
              [class.text-right]="col.align === 'right'"
            >
              <p-skeleton
                [width]="col.width"
                [height]="col.height ?? '1rem'"
                [shape]="col.shape === 'circle' ? 'circle' : 'rectangle'"
              />
            </td>
          }
        </tr>
      </ng-template>
    </p-table>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }

      :host ::ng-deep .p-datatable-skeleton {
        .p-datatable-thead > tr > th {
          background: var(--surface-ground, #f8f9fa);
          border-bottom: 1px solid var(--surface-border, #dee2e6);
          padding: 0.75rem 1rem;
        }

        .p-datatable-tbody > tr > td {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid var(--surface-border, #dee2e6);
        }

        .p-datatable-tbody > tr:hover {
          background: transparent;
        }
      }
    `,
  ],
})
export class TableSkeletonComponent {
  @Input() rows: number = 5;
  @Input() columns: TableSkeletonColumn[] = [];

  get rowsArray(): number[] {
    return Array.from({ length: this.rows }, (_, i) => i + 1);
  }

  getHeaderWidth(col: TableSkeletonColumn): string {
    if (col.shape === "circle") {
      return col.height ?? "1.5rem";
    }
    return col.width;
  }
}
